import { useEffect, useMemo, useRef, useState } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import { useWs } from 'contexts/WsContext';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import WarningOutlined from '@ant-design/icons/WarningOutlined';

// sx styles
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',
  transform: 'none'
};

function fmtTime(ts) {
  if (!ts) {
    const d = new Date();
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

// ambil preview text dari payload WS kamu
function pickPreviewText(raw) {
  if (!raw) return '';

  if (raw.type === 'chat_unread') {
    const s = raw.sessionName || '-';
    const cid = raw.chatId || '-';
    const unread = raw.unread ?? 0;
    return `Unread ${unread} • ${s} • ${cid}`;
  }

  const m = raw.message || raw?.data?.message || {};
  const txt = m.body ?? m.text ?? m.caption ?? raw.message ?? raw.text ?? '';
  if (txt && typeof txt === 'string') return txt;

  try {
    const s = JSON.stringify(raw);
    return s.length > 160 ? `${s.slice(0, 160)}…` : s;
  } catch {
    return String(raw);
  }
}

function buildTitle(raw) {
  if (!raw) return 'Event';
  if (raw.type === 'chat_unread') return 'Unread update';
  if (raw.type) return `WS ${raw.type}`;
  if (raw.direction) return `Message ${raw.direction}`;
  return 'WS event';
}

function buildSubTitle(raw) {
  const s = raw?.sessionName || '-';
  const cid = raw?.chatId || raw?.message?.from || raw?.message?.to || '';
  const parts = [];
  if (s) parts.push(`session: ${s}`);
  if (cid) parts.push(`chat: ${cid}`);
  return parts.join(' • ');
}

// ==============================|| HEADER CONTENT - NOTIFICATION (WS GLOBAL) ||============================== //

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const { events, isConnected, clearEvents } = useWs();
  const unreadCount = useMemo(() => {
    const list = Array.isArray(events) ? events : [];
    return list.filter((e) => e?.direction === 'incoming').length;
  }, [events]);

  const items = useMemo(() => {
    const list = Array.isArray(events) ? events : [];
    const incomingOnly = list.filter((e) => e?.direction === 'incoming');

    return incomingOnly.slice(0, 20).map((raw, i) => ({
      id: `${Date.now()}_${i}_${Math.random()}`,
      raw,
      title: 'Message incoming',
      subtitle: `session: ${raw?.sessionName || '-'} • chat: ${raw?.chatId || raw?.message?.from || '-'}`,
      preview: pickPreviewText(raw),
      time: fmtTime(raw?.createdAt || raw?.ts || raw?.time)
    }));
  }, [events]);

  const handleToggle = () => setOpen((v) => !v);

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={() => ({
          color: 'text.primary',
          bgcolor: open ? 'grey.100' : 'transparent'
        })}
        aria-label="open notification"
        ref={anchorRef}
        aria-controls={open ? 'notification-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={unreadCount} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>

      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 285, maxWidth: { xs: 285, md: 420 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6">Notification</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {isConnected ? 'WS: connected' : 'WS: disconnected'}
                      </Typography>
                    </Box>
                  }
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    <>
                      {items.length > 0 && (
                        <Tooltip title="Clear notifications">
                          <IconButton color="success" size="small" onClick={clearEvents}>
                            <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  }
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      '& .MuiListItemButton-root': {
                        py: 0.5,
                        px: 2,
                        '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                        '& .MuiAvatar-root': avatarSX,
                        '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                      }
                    }}
                  >
                    {items.length === 0 ? (
                      <Box sx={{ p: 2 }}>
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                          No notifications yet.
                        </Typography>
                      </Box>
                    ) : (
                      items.map((n) => (
                        <ListItem
                          key={n.id}
                          component={ListItemButton}
                          divider
                          secondaryAction={
                            <Typography variant="caption" noWrap>
                              {n.time}
                            </Typography>
                          }
                          onClick={() => {
                            console.log('[WS notif raw]', n.raw);
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ color: 'primary.main', bgcolor: 'primary.lighter' }}>
                              {n.raw?.type === 'chat_unread' ? <WarningOutlined /> : <MessageOutlined />}
                            </Avatar>
                          </ListItemAvatar>

                          <ListItemText
                            primary={<Typography variant="h6">{n.title}</Typography>}
                            secondary={
                              <Box>
                                {n.subtitle ? (
                                  <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }} noWrap>
                                    {n.subtitle}
                                  </Typography>
                                ) : null}
                                <Typography variant="body2" sx={{ display: 'block' }} noWrap>
                                  {n.preview}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))
                    )}
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
